'use client';

import {
  Paper,
  TextInput,
  PasswordInput,
  Checkbox,
  Button,
  Title,
  Text,
  Anchor,
  Divider,
  Box,
} from '@mantine/core';
import { CredentialResponse, GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import classes from './AuthenticationStep.module.css';
import { useAuth } from '@/contexts/UserContext';
import {
  type AuthUser,
  fetchAuthProviders,
  loginUser,
  oauthSignIn,
  registerUser,
} from '@/services/authService';

interface AuthenticationStepProps {
  isRegistration?: boolean;
}

const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '';

export function AuthenticationStep({ isRegistration = false }: AuthenticationStepProps) {
  const router = useRouter();
  const locale = useLocale();
  const { syncAuthSession } = useAuth();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleAuthEnabled, setIsGoogleAuthEnabled] = useState(Boolean(googleClientId));

  const postAuthPath = isRegistration ? `/${locale}/setup` : `/${locale}`;

  const switchAuthPath = useMemo(
    () => (isRegistration ? `/${locale}/login` : `/${locale}/register`),
    [isRegistration, locale]
  );

  useEffect(() => {
    let isActive = true;

    fetchAuthProviders()
      .then((providers) => {
        if (!isActive) {
          return;
        }

        setIsGoogleAuthEnabled(Boolean(providers.google) && Boolean(googleClientId));
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        setIsGoogleAuthEnabled(Boolean(googleClientId));
      });

    return () => {
      isActive = false;
    };
  }, []);

  const finalizeAuth = async (token: string, user: AuthUser, message: string) => {
    await syncAuthSession({ token, user }, { mergeAnonymousData: true });
    setSuccessMessage(message);
    router.push(postAuthPath);
    router.refresh();
  };

  const handleEmailAuth = async () => {
    setError(null);
    setSuccessMessage(null);

    if (isRegistration) {
      if (!fullName.trim()) {
        setError('Please enter your full name.');
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    if (!email.trim() || !password.trim()) {
      setError('Email and password are required.');
      return;
    }

    setIsSubmitting(true);

    try {
      const authResponse = isRegistration
        ? await registerUser({
            name: fullName.trim(),
            email: email.trim(),
            password,
          })
        : await loginUser({
            email: email.trim(),
            password,
          });

      await finalizeAuth(
        authResponse.token,
        authResponse.user,
        isRegistration
          ? 'Your account is ready. Redirecting to setup...'
          : 'Welcome back. Redirecting you now...'
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to continue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleAuth = async (response: CredentialResponse) => {
    if (!response.credential) {
      setError('Google did not return a credential. Please try again.');
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      const authResponse = await oauthSignIn({
        token: response.credential,
        provider: 'google',
      });

      await finalizeAuth(
        authResponse.token,
        authResponse.user,
        isRegistration
          ? 'Google sign-up successful. Redirecting to setup...'
          : 'Google sign-in successful. Redirecting now...'
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to continue with Google right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper className={classes.form} radius="lg">
      <Title
        order={2}
        className={`${classes.title} ${classes.textGradient}`}
        ta="center"
        mb="xs"
      >
        {isRegistration ? 'Create your account' : 'Sign in to your account'}
      </Title>
      <Text className={classes.subtitle} ta="center" mb="xl">
        {isRegistration
          ? 'It is quick to get started and continue your reset inside the app.'
          : 'Pick up right where you left off inside the Razee app.'}
      </Text>

      {isGoogleAuthEnabled && googleClientId && (
        <>
          <GoogleOAuthProvider clientId={googleClientId} locale={locale}>
            <Box className={classes.googleLogin}>
              <GoogleLogin
                onSuccess={handleGoogleAuth}
                onError={() => setError('Google sign-in was unsuccessful. Please try again.')}
                text={isRegistration ? 'signup_with' : 'signin_with'}
                theme="outline"
                shape="pill"
                size="large"
                width="100%"
              />
            </Box>
          </GoogleOAuthProvider>

          <Divider label="Or continue with email" labelPosition="center" my="lg" />
        </>
      )}

      {isRegistration && (
        <TextInput
          label="Full name"
          placeholder="Your full name"
          size="md"
          value={fullName}
          onChange={(event) => setFullName(event.currentTarget.value)}
        />
      )}

      <TextInput
        label="Email address"
        placeholder="hello@razee.app"
        size="md"
        mt={isRegistration ? 'md' : undefined}
        value={email}
        onChange={(event) => setEmail(event.currentTarget.value)}
      />

      <PasswordInput
        label="Password"
        placeholder="Your password"
        mt="md"
        size="md"
        value={password}
        onChange={(event) => setPassword(event.currentTarget.value)}
      />

      {isRegistration && (
        <PasswordInput
          label="Confirm password"
          placeholder="Confirm your password"
          mt="md"
          size="md"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.currentTarget.value)}
        />
      )}

      {!isRegistration && (
        <Checkbox
          label="Keep me logged in"
          mt="xl"
          size="md"
          checked={keepSignedIn}
          onChange={(event) => setKeepSignedIn(event.currentTarget.checked)}
        />
      )}

      {error && (
        <Text c="red" size="sm" mt="md">
          {error}
        </Text>
      )}

      {successMessage && (
        <Text c="green" size="sm" mt="md">
          {successMessage}
        </Text>
      )}

      <Button
        fullWidth
        mt="xl"
        size="md"
        loading={isSubmitting}
        onClick={handleEmailAuth}
      >
        {isRegistration ? 'Register' : 'Login'}
      </Button>

      <Text ta="center" mt="md">
        {isRegistration ? 'Already have an account? ' : "Don't have an account? "}
        <Anchor component={Link} href={switchAuthPath} fw={700}>
          {isRegistration ? 'Login' : 'Register'}
        </Anchor>
      </Text>
    </Paper>
  );
}
